import { useEffect, useState } from 'react'

import { Box, Link, useTheme } from '@mui/material'
import axios from 'axios'
import { InnerHtml, Loading } from 'components/ui'
import { useMappedTranslation } from 'hooks'
import i18n from 'utils/i18n'

import { appendLegacyParams } from './utils'

type Props = {
  title: string
  uri: string
  paramCountryCode?: string
  paramLocale?: string
}

export default function ConsentDetail({ title, uri, paramCountryCode, paramLocale }: Props) {
  const t = useMappedTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consentBody, setConsentBody] = useState<string>('<div/>')
  const theme = useTheme()

  // TODO: On stage, links should start with each per env domain.
  // All links in downloaded consent starts with production host.
  const LEGACY_HOST = 'https://account.samsung.com'

  useEffect(() => {
    axios
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')

        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')
        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        const body = document.getElementsByTagName('body').item(0)?.innerHTML
        if (body !== undefined) {
          const sanitized = body.replace(
            // due to KOR terms html include body padding set.
            'body { word-wrap:break-word; word-break:keep-all; padding:1.5em; }',
            'body { word-wrap:break-word; word-break:keep-all; }'
          )
          setConsentBody(sanitized)
        }
      })
      .catch(() => setConsentBody('<div>Failed to load</div>'))
      .finally(() => {
        setIsLoading(false)
      })
  }, [uri, paramLocale, paramCountryCode])

  const handleDownload = () => {
    const encodedHtmlContent =
      `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">` +
      `<html><head><meta http-equiv="Content-Type" content="text/html; charset=utf-8"></head>` +
      consentBody +
      '</html>'

    const blob: Blob = new Blob([encodedHtmlContent], { type: 'text/html; charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const aTag = document.createElement('a')
    aTag.href = url
    aTag.download = `${title}.html`
    aTag.click()
    aTag.remove()
    window.URL.revokeObjectURL(url)
  }

  return (
    <>
      <Box textAlign='right' marginTop={'53px'}>
        <Link
          component='button'
          type='button'
          color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050'}
          sx={{
            fontSize: '20px',
            fontHeight: '24px',
            fontWeight: '700px'
          }}
          onClick={handleDownload}
          data-testid='Link1A6ECBAD'
        >
          {t('common.button.download')}
        </Link>
      </Box>
      <Box sx={{ width: '100%' }} marginTop={{ xs: '74px', md: '36px' }}>
        {isLoading ? (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'tranlate(-50%, -50%)' }}>
            <Loading />
          </Box>
        ) : (
          <InnerHtml
            sx={{
              color: theme.palette.mode === 'dark' ? '#fefefe' : '#010101',
              fontSize: { xs: '15px', md: '16px' },
              letterSpacing: '0px',
              wordBreak: 'break-word'
            }}
          >
            {consentBody}
          </InnerHtml>
        )}
      </Box>
    </>
  )
}
